import { memo, useMemo } from 'react'
import { observer } from 'mobx-react'
import { Debtor } from '../../containers/PersonalForm/PersonalForm.interface'
import PathSelect from '../PathSelect'
import hasSecondDebtor from '../../utils/formDependencies/conditions/hasSecondDebtor'
import TraversalStore from '../../stores/TraversalStore'
import { useStores } from '../../stores/utils/hooks/useStores'
import Text from '../Input/Text'

const ACCOUNT_HOLDER_PATH = 'common.payoutAccountHolder'
const optionsTemplate = {
  [Debtor.PRIMARY]: {
    value: 'PRIMARY',
    debtors: [Debtor.PRIMARY]
  },
  [Debtor.SECONDARY]: {
    value: 'SECONDARY',
    debtors: [Debtor.SECONDARY]
  },
  both: {
    value: 'BOTH',
    debtors: [Debtor.PRIMARY, Debtor.SECONDARY]
  }
}

const getPersonalFactory =
  (traversal: TraversalStore, debtor: Debtor) => (property: string) =>
    traversal.getFieldValueByPath(`debtors.${debtor}.personal.${property}`)

const getFullName = (traversal: TraversalStore) => (debtor: Debtor) => {
  const personal = getPersonalFactory(traversal, debtor)

  return `${personal('firstname') || ''} ${personal('lastname') || ''}`
}

function buildOptions(
  traversal: TraversalStore,
  template: typeof optionsTemplate
) {
  return Object.values(template).map((option) => {
    const { debtors, value } = option

    return {
      value,
      label: debtors.map(getFullName(traversal)).join(', ')
    }
  })
}

const TextRender = memo(() => {
  const { traversal } = useStores()
  const defaultValue = useMemo(
    () => getFullName(traversal)(Debtor.PRIMARY),
    [traversal]
  )

  return <Text label="Kontoinhaber" defaultValue={defaultValue} disabled />
})

TextRender.displayName = 'TextRender'

const SelectRender = memo(() => {
  const { traversal } = useStores()
  const options = useMemo(
    () => buildOptions(traversal, optionsTemplate),
    [traversal]
  )

  return <PathSelect path={ACCOUNT_HOLDER_PATH} choices={options} />
})

SelectRender.displayName = 'SelectRender'

const AccountHolder = observer(() => {
  const {
    traversal: { data }
  } = useStores()
  if (hasSecondDebtor(data)) {
    return <SelectRender />
  }

  return <TextRender />
})

export default memo(AccountHolder)
